/**
 * The nine ticket categories, and the dictionary key that labels each (#165, #185).
 *
 * This is **not** `TicketType`, the priced product. The two sit in neighbouring
 * columns on the Tickets page and are routinely confused: on Connect+Grow 2026,
 * 41 tickets carry a product literally named "Vendégjegy" (guest ticket) while
 * all 41 are in the **Résztvevő** (participant) category. The category is
 * therefore the one the panel shows prominently.
 *
 * Before this map the only category labels in the panel lived inside
 * `EventAnalytics.tsx`, covered four of the values, and were reachable from
 * nowhere else. Five more surfaces need them now — the Tickets list, the
 * category filter, the ticket editor, the CSV import and the statistics — which
 * is exactly how `registrationStatus.ts` came about, and for the same reason:
 * one map, one dictionary section, so they cannot drift.
 *
 * **`VIP` is a category of its own — it is not the label of `SPONSOR`.** Until
 * 2026-09-17 it was: `SPONSOR` was shown as "VIP" everywhere. The client's final
 * list reversed that. `SPONSOR` is now "Támogató" (sponsor), and VIP is an
 * eighth stored value that the organizer fills in by hand; no code path writes
 * it. Anyone reaching for the old wording should know that labelling `SPONSOR`
 * "VIP" would now be a defect rather than a typo: two different tickets would
 * carry the same word on the same screen.
 *
 * The key is spelled out as a literal rather than built from the value, because
 * that is what lets `t()` and the dictionary tests see it.
 */
export type AttendeeType =
  | 'PARTICIPANT'
  | 'SPONSOR'
  | 'MEDIA_PARTNER'
  | 'GUEST'
  | 'EXHIBITOR'
  | 'ORGANIZER'
  | 'SPEAKER'
  | 'VIP'
  | 'TECHNICAL';

export const ATTENDEE_TYPE_LABEL_KEYS = {
  PARTICIPANT: 'attendeeType.participant',
  SPONSOR: 'attendeeType.sponsor',
  MEDIA_PARTNER: 'attendeeType.mediaPartner',
  GUEST: 'attendeeType.guest',
  EXHIBITOR: 'attendeeType.exhibitor',
  ORGANIZER: 'attendeeType.organizer',
  SPEAKER: 'attendeeType.speaker',
  VIP: 'attendeeType.vip',
  TECHNICAL: 'attendeeType.technical',
} as const satisfies Record<AttendeeType, string>;

/** The categories in database enum order — the order a category filter offers them in. */
export const ATTENDEE_TYPES = Object.keys(ATTENDEE_TYPE_LABEL_KEYS) as AttendeeType[];

/**
 * The key type, kept as the literal union rather than widened to `string`.
 *
 * `t()` in this project only accepts keys it can see in the dictionary, so
 * widening here would not merely lose precision — it would fail to compile at
 * every call site. That check is the compiler verifying that all nine labels
 * exist in `hu.json` and `en.json`.
 */
export type AttendeeTypeLabelKey = (typeof ATTENDEE_TYPE_LABEL_KEYS)[AttendeeType];

/**
 * The dictionary key for a category, or `null` if this build does not know the
 * value. The caller decides what to print instead — the statistics tile falls
 * back to the raw value, which reads worse than a label but is honest.
 *
 * `Object.hasOwn`, not `in` or a bare index: both walk the prototype chain, so
 * a value called `toString` or `constructor` would resolve to an inherited
 * member and hand `t()` a function instead of a key. The value arrives from the
 * API; it is not ours to assume what it cannot be.
 */
export function attendeeTypeLabelKey(value: string): AttendeeTypeLabelKey | null {
  return Object.hasOwn(ATTENDEE_TYPE_LABEL_KEYS, value)
    ? ATTENDEE_TYPE_LABEL_KEYS[value as AttendeeType]
    : null;
}
